import { i18n } from "src/boot/i18n"
import { getDB } from "src/db/databases"
import { useCacheStore } from "src/stores/cacheStore"
import { Log } from "src/util/classes/log"
import { GlobalNamedPath } from "src/util/enums/namedPaths"
import { getWorkDomainWorkspaceListData } from "src/util/functions/entityData"
import { getOpenCorrectiveWorkOnItemForIssue } from "src/util/functions/issues"
import { showWarningNotification } from "src/util/functions/notifications"
import { getIssuesOnItem } from "src/util/http/csi/issues"
import { getItem } from "src/util/http/csi/items"
import { getWorkDomainWorkspace } from "src/util/http/csi/workDomainsWorkspace"
import { NavigationGuardWithThis, RouteLocationNormalized } from "vue-router"

export const canPerformCorrectiveWork: NavigationGuardWithThis<undefined> = async (
  to,
  from,
  next
) => {
  const canNavigate = await canGoToCorrectiveWork(to, from)
  if (canNavigate) next()
  else {
    from.name ? next(false) : next({ name: GlobalNamedPath.NOT_FOUND })
  }
}

export const canPerformRegularWork: NavigationGuardWithThis<undefined> = async (to, from, next) => {
  const canNavigate = await canGoToRegularWork(to, from)
  if (canNavigate) next()
  else {
    from.name ? next(false) : next({ name: GlobalNamedPath.NOT_FOUND })
  }
}

async function canGoToCorrectiveWork(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  const { t } = i18n.global

  try {
    const issueId = to.params["issueId"]
    if (!issueId || typeof issueId !== "string") {
      Log.error({ message: "Issue Id on corrective work guard is incorrect", data: { to, from } })
      return false
    }

    const cacheStore = useCacheStore()
    const db = await getDB()

    const issueData =
      cacheStore.issues_on_item.get(issueId) ||
      (await db.issues_on_item.findOne(issueId).exec()) ||
      (await getIssuesOnItem({}, issueId))

    if (!issueData) {
      showWarningNotification(t("notifications.bad_issue_id"))
      return false
    }

    const workDomainData =
      cacheStore.work_domain_workspace.get(issueData.work_domain) ||
      (await db.work_domain_workspace.findOne(issueData.work_domain).exec()) ||
      (await getWorkDomainWorkspace({}, issueData.work_domain))

    const correctiveWork = await getOpenCorrectiveWorkOnItemForIssue(issueId)

    if (workDomainData?.allows_corrective_adhoc) {
      if (correctiveWork) {
        const msg = t("notifications.corrective_work_already_exists", {
          correctiveWorkTitle: correctiveWork.title,
        })
        showWarningNotification(msg)
        return false
      } else {
        return true
      }
    } else {
      const msg = t("notifications.corrective_work_not_allowed", {
        workDomain: workDomainData?.designation,
      })
      showWarningNotification(msg)
      return false
    }
  } catch (error) {
    Log.debug({ message: "Problem resolving corrective work guard", data: { to, from, error } })
    showWarningNotification(t("notifications.bad_corrective_work_guard"))
    return false
  }
}

async function canGoToRegularWork(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  const { t } = i18n.global

  try {
    const itemId = to.params["itemId"]
    if (!itemId || typeof itemId !== "string") {
      Log.error({ message: "Item Id on regular work guard is incorrect", data: { to, from } })
      return false
    }

    const cacheStore = useCacheStore()
    const db = await getDB()

    const itemData =
      cacheStore.item.get(itemId) ||
      (await db.item.findOne(itemId).exec()) ||
      (await getItem({}, itemId))

    if (!itemData) {
      showWarningNotification(t("notifications.bad_item_id"))
      return false
    }

    for (const workDomain of cacheStore.work_domain_workspace.values()) {
      if (workDomain.allows_regular_adhoc) return true
    }

    const workDomainData =
      (await db.work_domain_workspace.find().exec()) || (await getWorkDomainWorkspaceListData())

    if (workDomainData.some((workDomain) => workDomain.allows_regular_adhoc)) {
      return true
    }

    showWarningNotification(t("notifications.regular_work_not_allowed"))
    return false
  } catch (error) {
    Log.debug({ message: "Problem resolving regular work guard", data: { to, from, error } })
    showWarningNotification(t("notifications.bad_regular_work_guard"))
    return false
  }
}
